import * as CryptoJS from 'crypto-js';

export interface HmacHeaders {
  readonly 'x-signature': string;
  readonly 'x-timestamp': string;
}

export interface BuildHmacHeadersParams {
  readonly method: string;
  readonly url: string;
  readonly body: unknown;
  readonly secret: string;
  /** Optional override for tests; defaults to Date.now().toString() */
  readonly timestamp?: string;
}

/**
 * Returns pathname + search only (matches server `req.originalUrl` path+query).
 */
export function getApiPathWithQuery(url: string): string {
  if (!url) {
    return '';
  }

  try {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      const parsed = new URL(url);
      return `${parsed.pathname}${parsed.search}`;
    }

    // Relative URL: keep path + query as-is (drop fragment if present)
    const withoutHash = url.split('#')[0] ?? url;
    return withoutHash.startsWith('/') ? withoutHash : `/${withoutHash}`;
  } catch {
    return url;
  }
}

function serializeBodyForSign(body: unknown): string {
  if (body === null || body === undefined) {
    return '';
  }

  if (typeof body === 'string') {
    return body;
  }

  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    return '';
  }

  if (typeof Blob !== 'undefined' && body instanceof Blob) {
    return '';
  }

  if (body instanceof ArrayBuffer) {
    return '';
  }

  if (ArrayBuffer.isView(body)) {
    return '';
  }

  return JSON.stringify(body);
}

/**
 * Builds the payload portion signed with HMAC per method rules.
 * GET: `GET:{pathname}{?query}` — otherwise raw JSON body or empty string.
 */
export function getDataToSign(
  method: string,
  url: string,
  body: unknown
): string {
  const upperMethod = method.toUpperCase();

  if (upperMethod === 'GET') {
    return `GET:${getApiPathWithQuery(url)}`;
  }

  return serializeBodyForSign(body);
}

export function generateHmacSignature(message: string, secret: string): string {
  return CryptoJS.HmacSHA256(message, secret).toString(CryptoJS.enc.Hex);
}

export function buildHmacHeaders(params: BuildHmacHeadersParams): HmacHeaders {
  const timestamp = params.timestamp ?? Date.now().toString();
  const dataToSign = getDataToSign(params.method, params.url, params.body);
  const messageToSign = `${timestamp}:${dataToSign}`;
  const signature = generateHmacSignature(messageToSign, params.secret);

  return {
    'x-signature': signature,
    'x-timestamp': timestamp,
  };
}

/** True when the request targets the configured Logibids API base URL. */
export function isLogibidsApiRequest(
  requestUrl: string,
  baseUrl: string
): boolean {
  if (!requestUrl || !baseUrl) {
    return false;
  }

  return requestUrl.startsWith(baseUrl);
}
